/**
 * Prompt 模板引擎
 * 负责加载、解析和渲染 prompt 模板
 */

import fs from 'fs-extra';
import path from 'path';
import yaml from 'js-yaml';
import Handlebars from 'handlebars';
import { logger } from '../utils/logger';

export interface PromptVariable {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description?: string;
  required?: boolean;
  default?: any;
  enum?: string[];
}

export interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  version: string;
  variables: PromptVariable[];
  template: string;
  metadata?: {
    category?: string;
    tags?: string[];
    complexity?: string;
    effectiveness?: number;
  };
  examples?: any[];
}

export interface RenderOptions {
  strict?: boolean; // 严格模式：缺少必需变量时抛出错误
  helpers?: Record<string, Handlebars.HelperDelegate>; // 自定义 Handlebars helpers
}

export class PromptTemplateEngine {
  private templates: Map<string, PromptTemplate> = new Map();
  private promptDir: string;
  private handlebars: typeof Handlebars;
  
  constructor(workflowDir?: string) {
    const projectRoot = workflowDir || process.cwd();
    this.promptDir = path.join(projectRoot, '.workflow', 'prompts');
    
    // 初始化 Handlebars 并注册默认 helpers
    this.handlebars = Handlebars.create();
    this.registerDefaultHelpers();
  }
  
  /**
   * 初始化模板引擎
   */
  async initialize(): Promise<void> {
    await this.loadTemplates();
  }
  
  /**
   * 加载所有模板
   */
  private async loadTemplates(): Promise<void> {
    const templatesDir = path.join(this.promptDir, 'templates');
    
    if (!await fs.pathExists(templatesDir)) {
      logger.warn('Templates directory not found:', templatesDir);
      return;
    }
    
    const files = await fs.readdir(templatesDir);
    const templateFiles = files.filter(f => f.endsWith('.yaml') || f.endsWith('.yml'));
    
    for (const file of templateFiles) {
      try {
        const content = await fs.readFile(path.join(templatesDir, file), 'utf-8');
        const template = yaml.load(content) as PromptTemplate;
        
        if (template && template.id) {
          this.templates.set(template.id, template);
          logger.debug(`Loaded prompt template: ${template.id}`);
        }
      } catch (error) {
        logger.error(`Failed to load template from ${file}:`, error);
      }
    }
    
    logger.info(`Loaded ${this.templates.size} prompt templates`);
  }
  
  /**
   * 注册默认的 Handlebars helpers
   */
  private registerDefaultHelpers(): void {
    // 大写转换
    this.handlebars.registerHelper('uppercase', (str: string) => {
      return str ? str.toUpperCase() : '';
    });
    
    // 小写转换
    this.handlebars.registerHelper('lowercase', (str: string) => {
      return str ? str.toLowerCase() : '';
    });
    
    // 首字母大写
    this.handlebars.registerHelper('capitalize', (str: string) => {
      return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
    });
    
    // 日期格式化（简单版本）
    this.handlebars.registerHelper('formatDate', (date: string | Date, format: string) => {
      const d = new Date(date);
      if (isNaN(d.getTime())) return 'Invalid Date';
      
      // 简单的格式化实现
      return d.toISOString().split('T')[0];
    });
    
    // 数组长度
    this.handlebars.registerHelper('length', (arr: any[]) => {
      return Array.isArray(arr) ? arr.length : 0;
    });
    
    // 条件判断
    this.handlebars.registerHelper('eq', (a: any, b: any) => a === b);
    this.handlebars.registerHelper('ne', (a: any, b: any) => a !== b);
    this.handlebars.registerHelper('gt', (a: any, b: any) => a > b);
    this.handlebars.registerHelper('gte', (a: any, b: any) => a >= b);
    this.handlebars.registerHelper('lt', (a: any, b: any) => a < b);
    this.handlebars.registerHelper('lte', (a: any, b: any) => a <= b);
    
    // JSON 序列化
    this.handlebars.registerHelper('json', (obj: any) => {
      return JSON.stringify(obj, null, 2);
    });
  }
  
  /**
   * 获取所有模板
   */
  getTemplates(): PromptTemplate[] {
    return Array.from(this.templates.values());
  }
  
  /**
   * 获取指定模板
   */
  getTemplate(templateId: string): PromptTemplate | undefined {
    return this.templates.get(templateId);
  }
  
  /**
   * 按类别获取模板
   */
  getTemplatesByCategory(category: string): PromptTemplate[] {
    return this.getTemplates().filter(t => t.metadata?.category === category);
  }
  
  /**
   * 按标签获取模板
   */
  getTemplatesByTag(tag: string): PromptTemplate[] {
    return this.getTemplates().filter(t => 
      t.metadata?.tags?.includes(tag) || false
    );
  }
  
  /**
   * 验证变量值
   */
  private validateVariables(
    template: PromptTemplate, 
    variables: Record<string, any>
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    // 检查必需变量
    for (const varDef of template.variables) {
      if (varDef.required && !(varDef.name in variables)) {
        if (!varDef.default) {
          errors.push(`Missing required variable: ${varDef.name}`);
        }
      }
      
      // 类型检查
      if (varDef.name in variables) {
        const value = variables[varDef.name];
        const actualType = Array.isArray(value) ? 'array' : typeof value;
        
        if (actualType !== varDef.type && value !== null && value !== undefined) {
          errors.push(`Variable ${varDef.name} should be ${varDef.type} but got ${actualType}`);
        }
        
        // 枚举值检查
        if (varDef.enum && !varDef.enum.includes(value)) {
          errors.push(`Variable ${varDef.name} must be one of: ${varDef.enum.join(', ')}`);
        }
      }
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }
  
  /**
   * 填充默认值
   */
  private applyDefaults(
    template: PromptTemplate, 
    variables: Record<string, any>
  ): Record<string, any> {
    const result = { ...variables };
    
    for (const varDef of template.variables) {
      if (!(varDef.name in result) && varDef.default !== undefined) {
        result[varDef.name] = varDef.default;
      }
    }
    
    return result;
  }
  
  /**
   * 渲染模板
   */
  render(
    templateId: string, 
    variables: Record<string, any> = {}, 
    options: RenderOptions = {}
  ): string {
    const template = this.getTemplate(templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }
    
    // 应用默认值
    const varsWithDefaults = this.applyDefaults(template, variables);
    
    // 验证变量
    if (options.strict) {
      const validation = this.validateVariables(template, varsWithDefaults);
      if (!validation.valid) {
        throw new Error(`Template validation failed:\n${validation.errors.join('\n')}`);
      }
    }
    
    // 注册自定义 helpers
    if (options.helpers) {
      for (const [name, helper] of Object.entries(options.helpers)) {
        this.handlebars.registerHelper(name, helper);
      }
    }
    
    try {
      // 编译并渲染模板
      const compiledTemplate = this.handlebars.compile(template.template);
      return compiledTemplate(varsWithDefaults);
    } catch (error: any) {
      throw new Error(`Failed to render template ${templateId}: ${error.message}`);
    }
  }
  
  /**
   * 批量渲染模板（用于组合多个模板）
   */
  renderMultiple(
    templateSpecs: Array<{
      templateId: string;
      variables?: Record<string, any>;
    }>,
    options: RenderOptions = {}
  ): string[] {
    return templateSpecs.map(spec => 
      this.render(spec.templateId, spec.variables || {}, options)
    );
  }
  
  /**
   * 创建交互式渲染（收集用户输入）
   */
  async renderInteractive(
    templateId: string,
    getUserInput: (variable: PromptVariable) => Promise<any>,
    options: RenderOptions = {}
  ): Promise<string> {
    const template = this.getTemplate(templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }
    
    const variables: Record<string, any> = {};
    
    // 收集必需的变量值
    for (const varDef of template.variables) {
      if (varDef.required || !varDef.default) {
        const value = await getUserInput(varDef);
        if (value !== undefined) {
          variables[varDef.name] = value;
        }
      }
    }
    
    return this.render(templateId, variables, options);
  }
  
  /**
   * 导出模板为独立文件
   */
  async exportTemplate(
    templateId: string, 
    outputPath: string,
    includeMetadata = true
  ): Promise<void> {
    const template = this.getTemplate(templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }
    
    const exportData = includeMetadata ? template : {
      id: template.id,
      name: template.name,
      template: template.template
    };
    
    await fs.writeFile(outputPath, yaml.dump(exportData), 'utf-8');
  }
  
  /**
   * 渲染模板（别名方法）
   */
  async renderTemplate(
    templateId: string,
    variables: Record<string, any> = {},
    options: RenderOptions = {}
  ): Promise<string> {
    return this.render(templateId, variables, options);
  }
}